// Canonical client-facing route builders for the Publishing section
// (calendar, approvals, social publishing, sitemaps, media library).
//
// Every href, redirect, revalidatePath, and email deep link that points into
// these surfaces must come from this module, so a future route move is a
// one-file change. Pure string functions; safe to import from client
// components, server actions, and pure logic alike.

function base(clientId: string): string {
  return `/clients/${clientId}/publishing`;
}

export function publishingPath(clientId: string): string {
  return base(clientId);
}

export function publishingCalendarPath(clientId: string, day?: string): string {
  const path = `${base(clientId)}/calendar`;
  return day ? `${path}?d=${day}` : path;
}

export function publishingApprovalsPath(clientId: string): string {
  return `${base(clientId)}/approvals`;
}

/** Approval workflow CRUD for both posts and page content. */
export function approvalWorkflowsPath(clientId: string): string {
  return `${base(clientId)}/approvals/workflows`;
}

/**
 * The company-wide Content list: every scheduled post and page in one
 * filterable, paginated surface. The list-mode counterpart to the calendar.
 */
export function publishingContentPath(
  clientId: string,
  opts?: { kinds?: Array<"social" | "page">; view?: "cards" | "table" },
): string {
  const params = new URLSearchParams();
  if (opts?.kinds?.length) params.set("t", opts.kinds.join(","));
  // "table" is the list's default view, so it is never serialized.
  if (opts?.view === "cards") params.set("view", "cards");
  const qs = params.toString();
  return `${base(clientId)}/content${qs ? `?${qs}` : ""}`;
}

/**
 * The former social-only card grid. Gallery collapsed into the company-wide
 * Content list (2026-07-25): it was exactly that list filtered to posts in
 * cards mode. Kept as a builder so every existing href, redirect,
 * revalidatePath, and email deep link follows the move without edits.
 */
export function galleryPath(clientId: string): string {
  return publishingContentPath(clientId, { kinds: ["social"], view: "cards" });
}

// --- Social posts -----------------------------------------------------------

export function socialPostPath(clientId: string, postId: string, tab?: "approval" | "activity"): string {
  const path = `${base(clientId)}/social/posts/${postId}`;
  return tab ? `${path}?tab=${tab}` : path;
}

export function newSocialPostPath(clientId: string): string {
  return `${base(clientId)}/social/posts/new`;
}

/** Staff-only CSV import wizard (Gain exports). */
export function importSocialPostsPath(clientId: string): string {
  return `${base(clientId)}/social/import`;
}

export function editSocialPostPath(clientId: string, postId: string): string {
  return `${base(clientId)}/social/posts/${postId}/edit`;
}

// --- Sitemaps + content planner ---------------------------------------------

export function sitemapListPath(clientId: string): string {
  return `${base(clientId)}/sitemaps`;
}

export function newSitemapPath(clientId: string): string {
  return `${base(clientId)}/sitemaps/new`;
}

export function sitemapBuilderPath(
  clientId: string,
  sitemapId: string,
  versionId?: string | null,
  commentId?: string | null,
): string {
  const params = new URLSearchParams();
  if (versionId) params.set("v", versionId);
  if (commentId) params.set("comment", commentId);
  const qs = params.toString();
  return `${base(clientId)}/sitemaps/${sitemapId}${qs ? `?${qs}` : ""}`;
}

export function sitemapContentPath(clientId: string, sitemapId: string, versionId?: string | null): string {
  const path = `${base(clientId)}/sitemaps/${sitemapId}/content`;
  return versionId ? `${path}?v=${versionId}` : path;
}

export function sitemapExportPath(clientId: string, sitemapId: string, versionId: string): string {
  return `${base(clientId)}/sitemaps/${sitemapId}/export?v=${versionId}`;
}

export function plannerPagePath(
  clientId: string,
  sitemapId: string,
  pageId: string,
  commentId?: string | null,
): string {
  const path = `${base(clientId)}/sitemaps/${sitemapId}/pages/${pageId}`;
  return commentId ? `${path}?comment=${commentId}` : path;
}

// --- Media library ------------------------------------------------------------

export function mediaPath(clientId: string): string {
  return `${base(clientId)}/media`;
}
